import React, { useState, ReactNode, useContext } from 'react';

import { FeatureFlag } from 'constants-flags';
import { useFlag } from 'lane-shared/hooks';

import { ANALYTIC_KEYS } from 'constants-analytics';

import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useRouteMatch,
} from 'react-router-dom';

import { useAuth0 } from '@auth0/auth0-react';

import { AppContext, UserDataContext } from 'lane-shared/contexts';
import { useOnboardingContext } from 'lane-shared/contexts/OnboardingContext/useOnboardingContext';
import { Channel } from 'lane-shared/types/ChannelType';
import { useSimpleTrack } from 'lane-shared/hooks/useSimpleTrack';

import { OnboardingBuildingLegacy } from './OnboardingBuildingLegacy';
import { OnboardingBuilding } from './OnboardingBuilding';
import { OnboardingCompanyLegacy } from './OnboardingCompanyLegacy';
import { OnboardingCompany } from './OnboardingCompany';
import { OnboardingProfile } from './OnboardingProfile';
import { OnboardingWithInvite } from './OnboardingWithInvite';
import { OnboardingBuildingAndCompany } from './OnboardingBuildingAndCompany';

import { useWorkplaceOnboardingEnabled } from 'lane-shared/hooks/onboarding/useWorkplaceOnboardingEnabled';
import { useOnboardingUserInfo } from 'lane-shared/hooks/onboarding/useOnboardingUserInfo';
import { usePublicProfileChannel } from 'lane-shared/hooks/onboarding/usePublicProfileChannel';
import routes from 'lane-shared/config/routes';
import { ProfileData } from 'lane-shared/contexts/OnboardingContext';
import { OnboardingPhoneVerify } from './OnboardingPhoneVerify';

export type OnboardingCompleteData = {
  building?: Channel;
  company: Channel;
  userName: string;
};

const renderOnboardingBuildingOrInvite = ({
  OnboardingBuilding,
  OnboardingWithInvite,
  Redirect,
}: {
  OnboardingBuilding?: ReactNode;
  OnboardingWithInvite?: ReactNode;
  Redirect: ReactNode;
}) => {
  if (OnboardingWithInvite) {
    return OnboardingWithInvite;
  }

  if (OnboardingBuilding) {
    return OnboardingBuilding;
  }

  return Redirect;
};

export const OnboardingSteps = () => {
  const { path } = useRouteMatch();
  const history = useHistory();
  const { whitelabel } = useContext(AppContext);
  const simpleTrack = useSimpleTrack();
  const { user: auth0User, isAuthenticated } = useAuth0();
  const { user } = useContext(UserDataContext);

  const enhancedOnboardingFeatureFlag = useFlag(
    FeatureFlag.EnhancedOnboardingChannelSearch,
    false
  );
  const isPhoneNumberReqForSignupEnabled = useFlag(
    FeatureFlag.PhoneNumberReqForSignup,
    false
  );
  const isMergeBuildingAndCompanyEnabled = useFlag(
    FeatureFlag.OnboardingMergeBuildingAndCompany,
    false
  );
  const isWorkplaceOnboardingEnabled = useWorkplaceOnboardingEnabled();

  const isOnboarded = isAuthenticated && user?.roles && user.roles.length > 0;

  const {
    data: { building: invitedBuilding, company: invitedCompany, profile },
    errors,
    hasProfileNameBeenUpdated,
    onboardingState,
    handlers,
  } = useOnboardingContext();
  const [building, setBuilding] = useState<Channel | null>(
    invitedBuilding ?? null
  );

  const { presumedEmailDomain, userFullName, userFirstName, userLastName } =
    useOnboardingUserInfo({
      auth0User,
      profile,
      isInvited: Boolean(invitedBuilding ?? invitedCompany),
      shouldPrioritizeProfile: hasProfileNameBeenUpdated,
    });

  const publicProfileData = usePublicProfileChannel();

  const invitedOrPublicProfileBuilding = publicProfileData?.isBuilding
    ? publicProfileData.channel
    : invitedBuilding;

  const invitedOrPublicProfileCompany = publicProfileData?.isCompany
    ? publicProfileData.channel
    : invitedCompany;

  const handleRedirect = (pathname: string) => {
    const search = getQueryParameters(history);

    if (isWorkplaceOnboardingEnabled && pathname.includes('company')) {
      history.replace({
        pathname,
        search,
      });
    } else {
      history.push({
        pathname,
        search,
      });
    }
  };

  const handleProfileContinue = (data: ProfileData) => {
    simpleTrack(ANALYTIC_KEYS.ANALYTIC_USER_ONBOARDING_PROFILE_CONTINUE, {
      optIn: data.marketingOptIn,
    });
    handlers.updateProfile(data);

    if(data.phone && isPhoneNumberReqForSignupEnabled) {
      handlePhoneVerifyRedirect();
    } else {
      handleBuildingCompanyRedirect();
    }
   
  };

  const handlePhoneVerifyRedirect = () => {
    const url = `${path}/verify-phone-number`;

    handleRedirect(url);
  };

  const handleBuildingCompanyRedirect = () => {
    

    if (invitedOrPublicProfileBuilding) {
      setBuilding(invitedOrPublicProfileBuilding);

      if ((isWorkplaceOnboardingEnabled || isMergeBuildingAndCompanyEnabled) && invitedOrPublicProfileCompany) {
        handleCompanyComplete({
          company: invitedOrPublicProfileCompany,
          building: invitedOrPublicProfileBuilding,
          userName: userFullName || '',
        });

        return;
      }
    }

    // If merge building and company feature flag is enabled, redirect to combined page
    if (isMergeBuildingAndCompanyEnabled) {
      const url = `${path}/building-and-company`;

      handleRedirect(url);

      return;
    }

    const shouldRedirectToBuilding =
      (isWorkplaceOnboardingEnabled && !invitedOrPublicProfileBuilding) ||
      (!isWorkplaceOnboardingEnabled && building);

    // Clear building state if we're going to building page and it's not from invite/public profile
    if (shouldRedirectToBuilding && !invitedOrPublicProfileBuilding) {
      setBuilding(null);
    }

    const url = shouldRedirectToBuilding
      ? `${path}/building`
      : `${path}/company`;

    handleRedirect(url);
  };

  const handleBuildingContinue = (building: Channel) => {
    simpleTrack(ANALYTIC_KEYS.ANALYTIC_USER_ONBOARDING_BUILDING_CONTINUE, {
      buildingId: building._id,
    });
    setBuilding(building);

    if (isWorkplaceOnboardingEnabled && invitedOrPublicProfileCompany) {
      handleCompanyComplete({
        company: invitedOrPublicProfileCompany,
        building,
        userName: userFullName || '',
      });
    } else {
      handleRedirect(`${path}/company`);
    }
  };

  const handleCompanyBack = () => {
    const search = getQueryParameters(history);

    history.replace({
      pathname: `${path}/building`,
      search,
    });
  };

  const handleBuildingComplete = (building: Channel, fullName: string) => {
    setBuilding(building);
    simpleTrack(
      ANALYTIC_KEYS.ANALYTIC_USER_ONBOARDING_BUILDING_CREATE_ACCOUNT,
      {
        buildingId: building._id,
      }
    );

    const accountData = {
      fullName,
      marketingOptIn: profile.isMarketingOptInSelected,
      phone: profile.phone,
      location: profile.location,
      building,
    };

    handlers.completeOnboarding(accountData);
  };

  const handleCompanyComplete = ({
    company,
    building,
    userName,
  }: OnboardingCompleteData) => {
    if (building) {
      setBuilding(building);
    }

    simpleTrack('onboarding.building.createAccount', {
      buildingId: building?._id,
      companyId: company._id,
    });

    const accountData = {
      fullName: userName,
      marketingOptIn: profile.isMarketingOptInSelected,
      phone: profile.phone,
      location: profile.location,
      building,
      company,
    };

    handlers.completeOnboarding(accountData);
  };

  const handleCompanyCompleteLegacy = (
    company: Channel,
    building: Channel,
    fullName: string
  ) => {
    simpleTrack('onboarding.building.createAccount', {
      buildingId: building._id,
      companyId: company._id,
    });

    const accountData = {
      fullName,
      marketingOptIn: profile.isMarketingOptInSelected,
      phone: profile.phone,
      location: profile.location,
      building,
      company,
    };

    handlers.completeOnboarding(accountData);
  };

  const handleBuildingInviteComplete = (
    building: Channel,
    fullName: string,
    company?: Channel
  ) => {
    simpleTrack('onboarding.building.createAccount', {
      buildingId: building._id,
      companyId: company?._id,
    });

    const accountData = {
      fullName,
      marketingOptIn: profile.isMarketingOptInSelected,
      phone: profile.phone,
      location: profile.location,
      building,
      company,
    };

    handlers.completeOnboarding(accountData);
  };

  const handleOnChange = () => {
    handlers.onBuildingOrCompanySelectionChange();
  };

  if (isOnboarded) {
    return (
      <Redirect
        to={{
          pathname: `${routes.home}`,
        }}
      />
    );
  }

  const publicProfileChannel = publicProfileData
    ? publicProfileData.channel
    : undefined;

  const onboardingBuildingRender = enhancedOnboardingFeatureFlag ? (
    <OnboardingBuilding
      building={building}
      errorMessage={errors.completionError}
      userName={userFullName || ''}
      onboardingState={onboardingState}
      onComplete={handleBuildingComplete}
      onContinue={handleBuildingContinue}
      onOnboardingComplete={handleCompanyComplete}
      onClear={handleOnChange}
      invitedChannel={
        isWorkplaceOnboardingEnabled
          ? invitedOrPublicProfileCompany
          : publicProfileChannel
      }
      presumedEmailDomain={presumedEmailDomain}
    />
  ) : (
    <OnboardingBuildingLegacy
      errorMessage={errors.completionError}
      userName={userFullName || ''}
      onboardingState={onboardingState}
      onComplete={handleBuildingComplete}
      onClear={handleOnChange}
    />
  );

  const onboardingCompanyWithBuildingRender = enhancedOnboardingFeatureFlag ? (
    <OnboardingCompany
      errorMessage={errors.completionError}
      userName={userFullName || ''}
      onboardingState={onboardingState}
      whitelabel={whitelabel}
      onComplete={handleCompanyComplete}
      onCantFindCompany={handleBuildingComplete}
      onBack={handleCompanyBack}
      onClear={handleOnChange}
      invitedChannel={
        isWorkplaceOnboardingEnabled
          ? invitedOrPublicProfileBuilding
          : publicProfileChannel
      }
      buildingFromContext={building ?? null}
      presumedEmailDomain={presumedEmailDomain}
      companySelectionRequiredDuringSignup={
        whitelabel.settings?.companySelectionRequiredDuringSignup
      }
    />
  ) : (
    <OnboardingCompanyLegacy
      errorMessage={errors.completionError}
      userName={userFullName || ''}
      onboardingState={onboardingState}
      whitelabel={whitelabel}
      onComplete={handleCompanyCompleteLegacy}
      onClear={handleOnChange}
    />
  );

  const onboardingCompanyRender =
    isWorkplaceOnboardingEnabled && !building?._id
      ? onboardingBuildingRender
      : onboardingCompanyWithBuildingRender;

  return (
    <Switch>
      <Route exact path={`${path}/profile`}>
        <OnboardingProfile
          defaultUserName={userFullName}
          defaultFirstName={userFirstName}
          defaultLastName={userLastName}
          defaultPhone={profile.phone}
          defaultLocation={profile.location}
          onContinue={handleProfileContinue}
          whitelabel={whitelabel}
        />
      </Route>
      <Route exact path={`${path}/verify-phone-number`}>
        {profile.phone && user?._id ? (
          <OnboardingPhoneVerify
            phoneNumber={profile.phone}
            userLoginId={user._id}
            onBack={() => history.goBack()}
            onSuccess={handleBuildingCompanyRedirect}
          />
        ) : (
          <Redirect to={`${path}/profile`} />
        )}
      </Route>
      <Route exact path={`${path}/building-and-company`}>
        {isMergeBuildingAndCompanyEnabled && userFullName ? (
          <OnboardingBuildingAndCompany
            userName={userFullName}
            onboardingState={onboardingState}
            errorMessage={errors.completionError}
            onComplete={handleCompanyComplete}
            onBuildingComplete={handleBuildingComplete}
            onClear={handleOnChange}
            invitedBuilding={invitedOrPublicProfileBuilding}
            invitedCompany={invitedOrPublicProfileCompany}
            presumedEmailDomain={presumedEmailDomain}
            onCantFindCompany={handleBuildingComplete}
            companySelectionRequiredDuringSignup={
              whitelabel.settings?.companySelectionRequiredDuringSignup
            }
          />
        ) : (
          <Redirect to={`${path}/profile`} />
        )}
      </Route>
      <Route exact path={`${path}/building`}>
        {renderOnboardingBuildingOrInvite({
          OnboardingWithInvite:
            profile.fullName &&
            invitedBuilding &&
            !isWorkplaceOnboardingEnabled ? (
              <OnboardingWithInvite
                building={invitedBuilding}
                company={invitedCompany}
                userName={profile.fullName}
                onboardingState={onboardingState}
                errorMessage={errors.completionError}
                onComplete={handleBuildingInviteComplete}
                onClear={handleOnChange}
              />
            ) : undefined,
          OnboardingBuilding: userFullName
            ? onboardingBuildingRender
            : undefined,
          Redirect: <Redirect to={`${path}/profile`} />,
        })}
      </Route>
      <Route exact path={`${path}/company`}>
        {userFullName ? (
          onboardingCompanyRender
        ) : (
          <Redirect to={`${path}/profile`} />
        )}
      </Route>
    </Switch>
  );
};

function getQueryParameters(history: ReturnType<typeof useHistory>) {
  const currentQueryParameters = new URLSearchParams(history.location.search);
  const newQueryParameters = new URLSearchParams();

  const currentContentPath = currentQueryParameters.get('contentPath');
  const currentChannelId = currentQueryParameters.get('channelId');

  if (currentContentPath) {
    newQueryParameters.set('contentPath', currentContentPath);
  }

  if (currentChannelId) {
    newQueryParameters.set('channelId', currentChannelId);
  }

  return newQueryParameters.toString();
}